import React, { PropsWithChildren, useCallback, useRef, useState } from "react";
import {
    Active,
    ClientRect,
    Collision,
    CollisionDetection,
    DndContext,
    DragCancelEvent,
    DragEndEvent,
    DragMoveEvent,
    DragOverEvent,
    DragStartEvent,
    DroppableContainer,
    KeyboardSensor,
    Modifier,
    MouseSensor,
    rectIntersection,
    TouchSensor,
    UniqueIdentifier,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import type { Transform } from "@dnd-kit/utilities";
import { CollectionUtils } from "utilities/collection-utils";
import { DragAndDropScopeConfiguration } from "utilities/contexts/shared-drag-and-drop/drag-and-drop-scope-configuration";
import {
    DragAndDropScopeRegistryContext,
    DragAndDropScopeRegistryReactContext,
    UnregisterDragAndDropScope,
} from "utilities/contexts/shared-drag-and-drop/register-drag-and-drop-scope-context";
import { enforceScopedDragAndDropData } from "utilities/contexts/shared-drag-and-drop/scoped-drag-and-drop-data";
import {
    defaultSharedDragAndDropState,
    SharedDragAndDropContext,
    SharedDragAndDropState,
} from "utilities/contexts/shared-drag-and-drop/shared-drag-and-drop-context";
import { ShareDragAndDropOverlay } from "utilities/contexts/shared-drag-and-drop/shared-drag-and-drop-overlay";
import { RectMap } from "@dnd-kit/core/dist/store";

interface SharedDragAndDropContextProviderProps {}

type CollisionDetectionParameters = Parameters<CollisionDetection>[0];
type DragAndDropScopeRegistry = Record<string, DragAndDropScopeConfiguration>;
type DroppableCollisionProperties = Pick<
    CollisionDetectionParameters,
    "droppableContainers" | "droppableRects"
>;

const DEFAULT_COLLISION_DETECTION: CollisionDetection = rectIntersection;

export const SharedDragAndDropContextProvider: React.FC<
    PropsWithChildren<SharedDragAndDropContextProviderProps>
> = ({
    children,
}: PropsWithChildren<SharedDragAndDropContextProviderProps>): JSX.Element => {
    const dragAndDropScopeRegistryRef = useRef<DragAndDropScopeRegistry>({});
    const [sharedDragAndDropState, setSharedDragAndDropState] =
        useState<SharedDragAndDropState>(defaultSharedDragAndDropState);

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 10,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 150,
                tolerance: 5,
            },
        })
        //TODO - LINK-1648 Fix the issue of keyboard sensor interfering with typing in modals.

        // useSensor(KeyboardSensor, {
        //     coordinateGetter: sortableKeyboardCoordinates,
        // })
    );

    const registerDragAndDropScope = useCallback(
        (
            scopeName: string,
            configuration: DragAndDropScopeConfiguration
        ): UnregisterDragAndDropScope => {
            if (
                Object.keys(dragAndDropScopeRegistryRef.current).includes(
                    scopeName
                )
            ) {
                throw new Error(
                    `A Drag and Drop Scope with name ${scopeName} has already been registered`
                );
            }

            dragAndDropScopeRegistryRef.current = {
                ...dragAndDropScopeRegistryRef.current,
                [scopeName]: configuration,
            };

            return () => {
                if (
                    Object.keys(dragAndDropScopeRegistryRef.current).includes(
                        scopeName
                    )
                ) {
                    delete dragAndDropScopeRegistryRef.current[scopeName];
                }
            };
        },
        []
    );

    const getScopeNameByDraggable = useCallback(
        (active: Active): string | undefined => {
            const draggableData = enforceScopedDragAndDropData(
                active.data.current
            );

            return draggableData?.scope;
        },
        []
    );

    const getScopeConfigurationByDraggable = useCallback(
        (active: Active): DragAndDropScopeConfiguration | undefined => {
            const scope = getScopeNameByDraggable(active);

            return scope == null
                ? undefined
                : dragAndDropScopeRegistryRef.current[scope];
        },
        [getScopeNameByDraggable]
    );

    const dragAndDropScopeRegistryContext: DragAndDropScopeRegistryContext = {
        getScopeConfigurationByDraggable: getScopeConfigurationByDraggable,
        registerDragAndDropScope: registerDragAndDropScope,
    };

    const modifyByDraggableScope: Modifier = (args): Transform => {
        if (args.active == null) {
            return args.transform;
        }

        const configuration = getScopeConfigurationByDraggable(args.active);
        const modifiers = configuration?.contextModifiers;

        if (CollectionUtils.isEmpty(modifiers)) {
            return args.transform;
        }

        return modifiers.reduce(
            (accumulatedTransform: Transform, modifier: Modifier): Transform =>
                modifier({
                    ...args,
                    transform: accumulatedTransform,
                }),
            {
                ...args.transform,
            }
        );
    };

    const matchesDraggableScope = (scope: string, data: any): boolean => {
        const scopedData = enforceScopedDragAndDropData(data);

        if (scopedData == null) {
            return false;
        }

        return scopedData.scope === scope;
    };

    const filterToScopedDroppableRectangles = (
        rectangles: RectMap,
        scopedContainers: DroppableContainer[]
    ): RectMap => {
        const scopedContainerIds = scopedContainers.map((c) => c.id);
        const rectangleEntries = Array.from(rectangles.entries());
        const scopedRectangles = new Map<UniqueIdentifier, ClientRect>();

        rectangleEntries
            .filter(([id]) => scopedContainerIds.includes(id))
            .forEach(([id, rectangle]) => scopedRectangles.set(id, rectangle));

        return scopedRectangles;
    };

    const filterToScopedDroppableContainers = (
        scope: string | undefined,
        containers: DroppableContainer[]
    ): DroppableContainer[] => {
        if (scope == null || CollectionUtils.isEmpty(containers)) {
            return [];
        }

        return containers.filter((c) =>
            matchesDraggableScope(scope, c.data.current)
        );
    };

    const filterToScopedDroppables = (
        args: CollisionDetectionParameters
    ): DroppableCollisionProperties => {
        const scope = getScopeNameByDraggable(args.active);

        if (scope == null) {
            return {
                droppableContainers: [],
                droppableRects: new Map(),
            };
        }

        const scopedDroppableContainers = filterToScopedDroppableContainers(
            scope,
            args.droppableContainers
        );
        const scopedDroppableRectangles = filterToScopedDroppableRectangles(
            args.droppableRects,
            scopedDroppableContainers
        );

        return {
            droppableContainers: scopedDroppableContainers,
            droppableRects: scopedDroppableRectangles,
        };
    };

    const getCollisionDetectionAlgorithm = (
        active: Active
    ): CollisionDetection => {
        const configuration = getScopeConfigurationByDraggable(active);

        return configuration?.collisionDetection ?? DEFAULT_COLLISION_DETECTION;
    };

    const sharedCollisionDetection: CollisionDetection = (
        args: CollisionDetectionParameters
    ): Collision[] => {
        const collisionDetection = getCollisionDetectionAlgorithm(args.active);
        const scopedDroppables = filterToScopedDroppables(args);
        const scopedArgs = {
            ...args,
            ...scopedDroppables,
        };

        return collisionDetection(scopedArgs);
    };

    const handleDragStart = (event: DragStartEvent): void => {
        const configuration = getScopeConfigurationByDraggable(event.active);
        const draggableContent = configuration?.draggableComponent;
        const handler = configuration?.handleDragStart;

        if (handler != null) {
            handler(event);
        }

        setSharedDragAndDropState({
            DraggableContent: draggableContent,
            isDragging: true,
            isDropped: false,
        });
    };

    const handleDragMove = (event: DragMoveEvent): void => {
        const configuration = getScopeConfigurationByDraggable(event.active);
        const handler = configuration?.handleDragMove;

        if (handler != null) {
            handler(event);
        }
    };

    const handleDragOver = (event: DragOverEvent): void => {
        const configuration = getScopeConfigurationByDraggable(event.active);
        const handler = configuration?.handleDragOver;

        if (handler != null) {
            handler(event);
        }
    };

    const handleDragEnd = (event: DragEndEvent): void => {
        const configuration = getScopeConfigurationByDraggable(event.active);
        const handler = configuration?.handleDragEnd;

        if (handler != null) {
            handler(event);
        }

        setSharedDragAndDropState({
            isDragging: false,
            isDropped: true,
        });
    };

    const handleDragCancel = (event: DragCancelEvent): void => {
        const configuration = getScopeConfigurationByDraggable(event.active);
        const handler = configuration?.handleDragCancel;

        if (handler != null) {
            handler(event);
        }

        setSharedDragAndDropState({
            isDragging: false,
            isDropped: false,
        });
    };

    return (
        <SharedDragAndDropContext.Provider value={sharedDragAndDropState}>
            <DragAndDropScopeRegistryReactContext.Provider
                value={dragAndDropScopeRegistryContext}>
                <DndContext
                    collisionDetection={sharedCollisionDetection}
                    modifiers={[modifyByDraggableScope]}
                    onDragCancel={handleDragCancel}
                    onDragEnd={handleDragEnd}
                    onDragMove={handleDragMove}
                    onDragOver={handleDragOver}
                    onDragStart={handleDragStart}
                    sensors={sensors}>
                    {children}
                    <ShareDragAndDropOverlay />
                </DndContext>
            </DragAndDropScopeRegistryReactContext.Provider>
        </SharedDragAndDropContext.Provider>
    );
};
